/**
 * AdminOperationsShell — the new 2-tier navigation
 * =================================================
 *
 *  Top tier:  5 primary sections (Command Center · Dossiers · Members ·
 *             Security · Settings).
 *  Sub tier:  contextual tabs for the current section.
 *
 *  Header line shows: identity · Universal Search · Lock button · mobile menu.
 *
 *  All existing route components are mounted unchanged underneath — this is
 *  pure presentational reorganization.
 */

import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { LogOut, Menu, X, Search as SearchIcon, ClipboardList, ChevronDown } from "lucide-react";
import { ADMIN_SECTIONS, SECTION_TABS, sectionForPath } from "./adminSections";
import { adminRbacMe, adminHeroQueue } from "../../lib/api";
import UniversalSearch from "./UniversalSearch";
import OracleBell from "./OracleBell";
import { OracleProvider, useOracle } from "../../context/OracleContext";
import OracleBriefing, { openBriefing } from "./OracleBriefing";
import { CommandTrigger, MobileCommandTrigger } from "../../command/CommandTrigger";

const sectionLinkCls = ({ isActive }) =>
  `inline-flex items-center gap-2 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.28em] border-b-2 transition-colors ${
    isActive
      ? "border-[#D92D20] text-[#E4DFD3]"
      : "border-transparent text-[#A39E93] hover:text-[#E4DFD3] hover:border-[#D92D20]/40"
  }`;

const subTabCls = ({ isActive }) =>
  `inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em] border ${
    isActive
      ? "border-[#D92D20] text-[#E4DFD3] bg-[#1a0808]"
      : "border-[#2D2A26] text-[#A39E93] hover:border-[#D92D20] hover:text-[#E4DFD3]"
  }`;

const mobileLinkCls = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-[0.25em] border-b border-[#2D2A26] ${
    isActive ? "text-[#D92D20] bg-[#1a0808]" : "text-[#E4DFD3] hover:text-[#D92D20]"
  }`;

export const AdminOperationsShell = ({ identity, onSignOut, opToken, userToken, children }) => (
  <OracleProvider opToken={opToken} userToken={userToken}>
    <AdminShellInner identity={identity} onSignOut={onSignOut} opToken={opToken} userToken={userToken}>
      {children}
    </AdminShellInner>
  </OracleProvider>
);

const SectionBadge = ({ sk }) => {
  const { sectionBadges } = useOracle();
  const info = sectionBadges?.[sk];
  if (!info || !info.count) return null;  const color = info.worst === "critical" ? "#D92D20" : info.worst === "warning" ? "#E0A526" : info.worst === "info" ? "#3B82F6" : "#3FB950";
  const worstLabel = info.worst || "info";
  return (
    <span
      data-testid={`section-badge-${sk}`}
      aria-live="polite"
      aria-label={`${info.count} unread ${worstLabel} notifications in ${sk}`}
      className="ml-1 min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center border border-[#0a0907] font-mono text-[9px] font-bold"
      style={{ background: color, color: "#0a0907" }}
    >{info.count > 99 ? "99+" : info.count}</span>
  );
};

/* NEEDS HERO REVIEW badge — publish-gate visibility on the Dossiers
   section link + the Hero Review sub-tab. Polls the queue count. */
const HeroQueueBadge = ({ opToken, userToken }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const pull = () =>
      adminHeroQueue(opToken || null, userToken || null)
        .then((d) => { if (!cancelled) setCount(d?.count || 0); })
        .catch(() => { /* silent */ });
    pull();
    const t = setInterval(pull, 90000);
    return () => { cancelled = true; clearInterval(t); };
  }, [opToken, userToken]);
  if (!count) return null;
  return (
    <span
      data-testid="hero-review-nav-badge"
      aria-label={`${count} dossiers need hero review`}
      title={`${count} dossiers need hero review`}
      className="ml-1 min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center border border-[#0a0907] font-mono text-[9px] font-bold"
      style={{ background: "#E0A526", color: "#0a0907" }}
    >{count > 99 ? "99+" : count}</span>
  );
};

const HealthChip = () => {
  const { summary } = useOracle();
  if (!summary?.health) return null;
  const { score, label, critical, warning } = summary.health;
  const color = score >= 90 ? "#3FB950" : score >= 70 ? "#E0A526" : "#D92D20";
  const tip = critical || warning
    ? `Archive Health · ${critical} critical, ${warning} warning unresolved`
    : "Archive Health · everything operational";
  return (
    <>
      {/* Desktop: score + label */}
      <Link
        to="/admin/security/oracle"
        title={tip}
        aria-label={tip}
        data-testid="archive-health-chip"
        className="hidden md:inline-flex items-center gap-1.5 px-2 py-1 border border-[#2D2A26] hover:border-[#D92D20] font-mono text-[9px] uppercase tracking-[0.2em]"
        style={{ color }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
        {score}% · {label}
      </Link>
      {/* Mobile: dot-only compact */}
      <Link
        to="/admin/security/oracle"
        title={tip}
        aria-label={tip}
        data-testid="archive-health-chip-mobile"
        className="md:hidden inline-flex items-center justify-center w-9 h-9 border border-[#2D2A26] hover:border-[#D92D20]"
      >
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      </Link>
    </>
  );
};

const BriefingButton = () => (
  <button
    type="button"
    onClick={openBriefing}
    aria-label="Reopen daily operations briefing"
    title="Daily Briefing"
    data-testid="oracle-briefing-reopen"
    className="hidden md:inline-flex items-center justify-center w-9 h-9 border border-[#2D2A26] text-[#A39E93] hover:border-[#D92D20] hover:text-[#D92D20] transition-colors"
  >
    <ClipboardList size={15} />
  </button>
);

const AdminShellInner = ({ identity, onSignOut, opToken, userToken, children }) => {
  const loc = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => setDrawerOpen(false), [loc.pathname]);
  useEffect(() => {
    // Always restore overflow on unmount as a safety net.
    return () => { document.body.style.overflow = ""; };
  }, []);
  useEffect(() => {
    if (!drawerOpen) {
      document.body.style.overflow = "";
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  // Owner gate for owner-only sub-tabs (the shell, not the API — backend is
  // the source of truth).
  useEffect(() => {
    let cancelled = false;
    adminRbacMe(opToken || null, userToken || null)
      .then((me) => { if (!cancelled) setIsOwner(!!me?.is_owner); })
      .catch(() => { if (!cancelled) setIsOwner(false); });
    return () => { cancelled = true; };
  }, [opToken, userToken]);

  const activeSection = sectionForPath(loc.pathname);
  const subTabs = useMemo(
    () => (SECTION_TABS[activeSection] || []).filter((t) => !t.ownerOnly || isOwner),
    [activeSection, isOwner],
  );

  // Grouped sub-navigation — large sections declare `group` on their tabs and
  // render as compact dropdown clusters instead of one long flat strip.
  const grouped = useMemo(() => {
    const m = {};
    subTabs.forEach((t) => {
      const g = t.group || "General";
      (m[g] = m[g] || []).push(t);
    });
    return m;
  }, [subTabs]);
  const groupNames = useMemo(() => Object.keys(grouped), [grouped]);
  const hasGroups = useMemo(() => subTabs.some((t) => t.group), [subTabs]);
  const activeGroup = useMemo(
    () => (subTabs.find((t) => loc.pathname.startsWith(t.to))?.group) || groupNames[0] || null,
    [subTabs, loc.pathname, groupNames],
  );
  const [openGroup, setOpenGroup] = useState(activeGroup);
  useEffect(() => { setOpenGroup(activeGroup); }, [activeGroup]);

  const identityLabel = identity?.source === "token" ? "OPERATOR" : (identity?.email || "");

  return (
    <div className="min-h-screen bg-[#0a0907] text-[#E4DFD3]" data-testid="admin-shell">
      {/* === Header band === */}
      <header className="border-b border-[#D92D20]/20 bg-[#0F0F0F] sticky top-0 z-30">
        {/* Top row: brand + search + identity + lock */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-3 flex items-center gap-4">
          <NavLink
            to="/admin/command"
            data-testid="admin-shell-brand"
            className="font-display text-xl tracking-wide text-[#E4DFD3] hover:text-[#D92D20] shrink-0"
          >
            THE ARCHIVE
            <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] align-middle">
              // OPS
            </span>
          </NavLink>

          {/* Universal search */}
          <div className="hidden md:block flex-1 max-w-xl mx-auto">
            <UniversalSearch
              activeSection={activeSection}
              opToken={opToken}
              userToken={userToken}
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            <CommandTrigger className="hidden md:flex" />
            <HealthChip />
            <BriefingButton />
            <OracleBell opToken={opToken} userToken={userToken} />
            <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.25em] text-[#757065]">
              {identityLabel}
            </span>
            <button
              onClick={onSignOut}
              data-testid="admin-shell-signout"
              className="flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em]"
            >
              <LogOut size={11} /> Lock
            </button>
            <button
              onClick={() => setDrawerOpen((v) => !v)}
              aria-label="Open admin menu"
              data-testid="admin-mobile-menu-toggle"
              className="lg:hidden flex items-center justify-center w-10 h-10 border border-[#454038] hover:border-[#D92D20] text-[#E4DFD3] hover:text-[#D92D20]"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>

        {/* Mobile search */}
        <div className="md:hidden max-w-[1400px] mx-auto px-4 pb-3">
          <UniversalSearch
            activeSection={activeSection}
            opToken={opToken}
            userToken={userToken}
          />
        </div>

        {/* Section bar (desktop) */}
        <nav className="hidden lg:flex max-w-[1400px] mx-auto px-6 md:px-10 gap-1" data-testid="admin-section-bar">
          {ADMIN_SECTIONS.map((s) => (
            <NavLink
              key={s.key}
              to={s.to}
              data-testid={s.testid}
              className={sectionLinkCls}
              end={s.key === "command"}
            >
              <s.icon size={12} />
              {s.label}
              <SectionBadge sk={s.key} />
              {s.key === "dossiers" && <HeroQueueBadge opToken={opToken} userToken={userToken} />}
            </NavLink>
          ))}
        </nav>

        {/* Sub-tabs strip — grouped dropdown clusters for large sections */}
        {subTabs.length > 1 && (
          <div className="hidden lg:block border-t border-[#2D2A26]">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-2" data-testid="admin-subtab-bar">
              {hasGroups ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  {groupNames.map((g) => (
                    <button
                      key={g}
                      type="button"
                      data-testid={`subtab-group-${g.toLowerCase()}`}
                      onClick={() => setOpenGroup(openGroup === g ? null : g)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em] border transition-colors ${
                        g === activeGroup
                          ? "border-[#D92D20] text-[#E4DFD3] bg-[#1a0808]"
                          : openGroup === g
                            ? "border-[#454038] text-[#E4DFD3]"
                            : "border-[#2D2A26] text-[#A39E93] hover:border-[#D92D20]/60 hover:text-[#E4DFD3]"
                      }`}
                    >
                      {g}
                      <ChevronDown
                        size={10}
                        className={`transition-transform ${openGroup === g ? "rotate-180" : ""}`}
                      />
                    </button>
                  ))}
                  {openGroup && grouped[openGroup] && (
                    <>
                      <span className="w-px h-4 bg-[#2D2A26] mx-1" aria-hidden="true" />
                      {grouped[openGroup].map((t) => (
                        <NavLink
                          key={t.to}
                          to={t.to}
                          data-testid={t.testid}
                          className={subTabCls}
                        >
                          {t.icon && <t.icon size={10} />}
                          {t.label}
                          {t.to === "/admin/dossiers/hero-review" && (
                            <HeroQueueBadge opToken={opToken} userToken={userToken} />
                          )}
                        </NavLink>
                      ))}
                    </>
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {subTabs.map((t) => (
                    <NavLink
                      key={t.to}
                      to={t.to}
                      data-testid={t.testid}
                      className={subTabCls}
                    >
                      {t.icon && <t.icon size={10} />}
                      {t.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mobile current-section indicator */}
        <div className="lg:hidden max-w-[1400px] mx-auto px-4 pb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
          // {ADMIN_SECTIONS.find((s) => s.key === activeSection)?.label || "Admin"}
        </div>
      </header>

      {/* === Mobile drawer === */}
      {drawerOpen && (
        <button
          type="button"
          aria-label="Close admin menu"
          onClick={() => setDrawerOpen(false)}
          className="lg:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          data-testid="admin-mobile-drawer-overlay"
        />
      )}
      <aside
        data-testid="admin-mobile-drawer"
        aria-hidden={!drawerOpen}
        className={`lg:hidden fixed top-0 right-0 bottom-0 z-50 w-[82%] max-w-sm bg-[#0F0F0F] border-l border-[#D92D20]/40 shadow-2xl transform transition-transform duration-200 ease-out ${
          drawerOpen ? "translate-x-0" : "translate-x-full pointer-events-none"
        }`}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2D2A26]">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
            // OPERATIONS
          </div>
          <button
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
            data-testid="admin-mobile-drawer-close"
            className="flex items-center justify-center w-9 h-9 border border-[#454038] text-[#A39E93] hover:border-[#D92D20] hover:text-[#D92D20]"
          >
            <X size={16} />
          </button>
        </div>
        <nav className="flex flex-col overflow-y-auto h-[calc(100%-52px)]" data-testid="admin-mobile-nav">
          {ADMIN_SECTIONS.map((s) => {
            const sectionTabs = (SECTION_TABS[s.key] || []).filter((t) => !t.ownerOnly || isOwner);
            const isActiveSection = activeSection === s.key;
            return (
              <div key={s.key} className="border-b border-[#2D2A26]">
                <NavLink
                  to={s.to}
                  data-testid={`mobile-${s.testid}`}
                  className={mobileLinkCls}
                  end={s.key === "command"}
                >
                  <s.icon size={14} />
                  {s.label}
                  <SectionBadge sk={s.key} />
                </NavLink>
                {/* Mobile: only the ACTIVE section expands its sub-tabs
                    (grouped with small headers) — keeps the drawer short.
                    Tapping another section navigates there, which expands it. */}
                {isActiveSection && sectionTabs.length > 0 && (
                  <div className="bg-[#0a0907]">
                    {(() => {
                      let lastGroup = null;
                      return sectionTabs.map((t) => {
                        const showHeader = t.group && t.group !== lastGroup;
                        if (t.group) lastGroup = t.group;
                        return (
                          <React.Fragment key={t.to}>
                            {showHeader && (
                              <div className="px-7 pt-3 pb-1 font-mono text-[8px] uppercase tracking-[0.35em] text-[#757065]">
                                // {t.group}
                              </div>
                            )}
                            <NavLink
                              to={t.to}
                              data-testid={`mobile-${t.testid}`}
                              className={({ isActive }) =>
                                `flex items-center gap-3 px-7 py-2 font-mono text-[10px] uppercase tracking-[0.22em] border-b border-[#2D2A26]/40 ${
                                  isActive ? "text-[#D92D20] bg-[#1a0808]" : "text-[#A39E93] hover:text-[#E4DFD3]"
                                }`
                              }
                            >
                              {t.icon ? <t.icon size={11} /> : <SearchIcon size={11} className="opacity-0" />}
                              {t.label}
                            </NavLink>
                          </React.Fragment>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            );
          })}
          <div className="mt-2 px-4 py-3 border-t border-[#2D2A26]">
            <MobileCommandTrigger onNavigate={() => setDrawerOpen(false)} />
          </div>
          <div className="mt-2 px-4 py-3 border-t border-[#2D2A26]">
            <button
              type="button"
              onClick={() => { openBriefing(); setDrawerOpen(false); }}
              data-testid="mobile-oracle-briefing-reopen"
              className="w-full flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-[#A39E93] hover:text-[#D92D20]"
            >
              <ClipboardList size={12} /> Daily Briefing
            </button>
          </div>
          <div className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#757065] border-t border-[#2D2A26]">
            {identityLabel}
          </div>
        </nav>
      </aside>

      {/* === Main viewport === */}
      <main
        className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-6 md:py-10"
        data-testid="admin-main-viewport"
      >
        {children}
      </main>

      {/* Oracle daily briefing — session-scoped, closes on X */}
      <OracleBriefing opToken={opToken} userToken={userToken} identity={identity} />
    </div>
  );
};

export default AdminOperationsShell;
